import React, { useRef, useState, useEffect } from "react";
import { RiExpandUpDownLine } from "react-icons/ri";
import { LinearProgress, Box, Button, Grid, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import useAuth from "../context/AuthProvider";
import { DataGridPro, GridColDef, GridRowId, GridSelectionModel, GridSortModel } from "@mui/x-data-grid-pro";
import { getRowsPerPageOptions } from "../components/AddEdit/CustomFunctions";
import { apiClient } from "../helper/api";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";
// @ts-ignore
import html2pdf from "html2pdf.js";
import moment from "moment";

interface NonPremiumEndorsementModel {
  id?: number;
  nonPremiumEndorsementId?: number;
  client?: string;
  customerName?: string;
  state?: string;
  filingState?: string;
  homeState?: string;
  document?: string;
  documentName?: string;
  blobAddress?: string;
  dateReceived?: string;
  dateRecived?: string;
  dateCompleted?: string;
  status?: string;
}

interface NonPremiumEndorsementModel_Resp {
  getNonPremiumEndorsement_Respdata: NonPremiumEndorsementModel[];
  totalCount: number;
}

interface Iprops {
  ReportModuleCL: NonPremiumEndorsementModel_Resp;
  setSelectedRows: React.Dispatch<React.SetStateAction<NonPremiumEndorsementModel[] | null>>
  selectedRows: NonPremiumEndorsementModel[] | null
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>
  setPageSize: React.Dispatch<React.SetStateAction<number>>
  _pageSize: number
  filterModel: { items: { columnField: string, value: string, operatorValue: string }[], logicOperator?: 'and' | 'or' }
  setFilterModel: React.Dispatch<React.SetStateAction<{ items: { columnField: string, value: string, operatorValue: string }[], logicOperator?: 'and' | 'or' }>>
  setSortModel: React.Dispatch<React.SetStateAction<{ field: string, sort: string }[]>>
  loading: boolean
  selectedTab: number
}

export const NonPremiumEndorsementList: React.FC<Iprops> = ({
  ReportModuleCL,
  setSelectedRows,
  selectedRows,
  setCurrentPage,
  setPageSize,
  _pageSize,
  filterModel,
  setFilterModel,
  setSortModel,
  loading,
  selectedTab
}) => {
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const columns: GridColDef[] = [
    {
      field: "client", headerName: "Client", type: "string", headerAlign: "center", hideSortIcons: true, renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ), align: "center", flex: 3,
      valueGetter: (params) => params.row.client || params.row.customerName || "",
    },
    {
      field: "state", headerName: "State", type: "string", headerAlign: "center", hideSortIcons: true, renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ), align: "center", flex: 2,
      valueGetter: (params) => params.row.state || params.row.filingState || params.row.homeState || "",
    },
    {
      field: "document", headerName: "Document", type: "string", headerAlign: "center", hideSortIcons: true, renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ), align: "center", flex: 3, minWidth: 200,
      renderCell: (params) => {
        const documentName = params.row.document || params.row.documentName || "";
        const blobAddress = params.row.blobAddress;
        
        if (blobAddress && documentName) {
          return (
            <a 
              href={blobAddress} 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ 
                color: '#162E3C', 
                textDecoration: 'none', 
                overflow: 'initial', 
                whiteSpace: 'break-spaces',
                lineHeight: 'normal',
                wordBreak: 'break-word'
              }}
            >
              {documentName}
            </a>
          );
        }
        return (
          <Box sx={{ 
            width: '100%', 
            overflow: 'initial', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'break-spaces',
            lineHeight: 'normal',
            wordBreak: 'break-word'
          }}>
            {documentName || "-"}
          </Box>
        );
      },
    },
    {
      field: "dateReceived", headerName: "Date Received", type: "date", headerAlign: "center", hideSortIcons: true, renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ), align: "center", flex: 3,
      valueGetter: (params) => {
        const dateValue = params.row.dateReceived || params.row.dateRecived || "";
        return dateValue ? new Date(dateValue) : null;
      },
      renderCell: (params) => {
        if (!params.value) return "";
        const date = new Date(params.value);
        if (date.getFullYear() === 1900) return "";
        return date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
      },
    },
    {
      field: "dateCompleted", headerName: "Date Filed", type: "date", headerAlign: "center", hideSortIcons: true, renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ), align: "center", flex: 3,
      valueGetter: (params) => {
        const dateValue = params.row.dateCompleted || "";
        return dateValue ? new Date(dateValue) : null;
      },
      renderCell: (params) => {
        if (!params.value) return "";
        const date = new Date(params.value);
        if (date.getFullYear() === 1900) return "";
        return date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
      },
      hide: selectedTab !== 2,
    },
  ];

  const sortModelRef = useRef<GridSortModel>([]);
  const onRowsSelectionHandler = (ids: GridSelectionModel | undefined) => {
    try {
      if (!ids || ids.length === 0) {
        setSelectedRows(null);
        return;
      }
      const selectedRows = ReportModuleCL.getNonPremiumEndorsement_Respdata.filter((row) => ids.includes(row.id ?? row.nonPremiumEndorsementId ?? 0));
      setSelectedRows(selectedRows);
    } catch (err) {
      console.error('Error in onRowsSelectionHandler:', err);
      setError('Error processing row selection');
    }
  };

  function EditToolbar() {
  return (
      <React.Fragment>
        <div style={{ position: "fixed", top: "0", left: "0", zIndex: "9990", width: "100%" }}>
          {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
        </div>
      </React.Fragment>
    )
  }
  
  return (
    <React.Fragment>
      <div style={{ height: "50vh", overflowX: "scroll" }}>
        <DataGridPro
          getRowId={(r) => r?.id || r?.nonPremiumEndorsementId}
          rows={ReportModuleCL.getNonPremiumEndorsement_Respdata}
          columns={columns}
          rowCount={ReportModuleCL.totalCount || 0}
          rowsPerPageOptions={getRowsPerPageOptions(ReportModuleCL?.totalCount, _pageSize)}
          pagination
          pageSize={_pageSize}
          paginationMode="server"
          onPageChange={(newPage: number) => setCurrentPage(newPage + 1)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={(newFilterModel: any) => setFilterModel(newFilterModel)}
          sortingMode="server"
          sortingOrder={['asc', 'desc']}
          onSortModelChange={(newSortModel: any) => setSortModel(newSortModel)}
          style={{ textAlign: "center" }}
          checkboxSelection={
            user?.userRoles.split(",").includes("Super Admin") ||
            user?.userRoles.split(",").includes("MISC State Reporter")
              ? true : false
          }
          onSelectionModelChange={user?.userRoles.split(",").includes("Super Admin") ||
            user?.userRoles.split(",").includes("MISC State Reporter") ? (newRowSelectionModel) => {
              onRowsSelectionHandler(newRowSelectionModel);
            } : undefined}
          selectionModel={selectedRows?.map((row) => row.id || row.nonPremiumEndorsementId).filter((id): id is number => id !== undefined) || []}
          components={{
            Toolbar: EditToolbar,
          }}
          sx={{
            border: "0px",
            fontSize: "16px",
            borderRadius: '10px',
            overflow: 'hidden',
            fontFamily: "'Proxima Nova', sans-serif",
            '& .MuiDataGrid-main': {
              overflow: 'hidden'
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              overflow: 'visible',
              whiteSpace: 'normal',
              lineHeight: 'normal',
              wordWrap: 'break-word',
              textOverflow: 'clip',
            },
            '& .MuiDataGrid-cell': {
              overflow: 'visible',
              whiteSpace: 'normal',
              lineHeight: 'normal',
              wordWrap: 'break-word',
              textOverflow: 'clip',
              padding: '8px 16px',
            },
            '& .MuiDataGrid-cellContent': {
              overflow: 'visible',
              whiteSpace: 'normal',
              lineHeight: 'normal',
              wordWrap: 'break-word',
              textOverflow: 'clip',
            },
            ".custom-cell": {
              button: {
                padding: 0,
                width: '100%',
                height: '100%',
                textAlign: 'center',
                WebkitLineClamp: 2,
                lineClamp: 2,
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                textOverflow: 'ellipsis',
              }
            },
          }}
          experimentalFeatures={{ newEditingApi: true }}
        />
      </div>
    </React.Fragment>
  );
};

// Page Component
export const NonPremiumEndorsement: React.FC = () => {
  const { user } = useAuth();
  const token = user?.token;
  const params = useParams();
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(1);
  const [selectedClient, setSelectedClient] = useState(0);
  const [selectedHomeState, setSelectedHomeState] = useState(0);
  const [selectedRows, setSelectedRows] = useState<NonPremiumEndorsementModel[] | null>(null);
  
  // Pagination state
  const [_currentPage, setCurrentPage] = useState(1);
  const [_pageSize, setPageSize] = useState(50);
  
  // Filter and sort models
  const [filterModel, setFilterModel] = useState<{ items: { columnField: string, value: string, operatorValue: string }[], logicOperator?: 'and' | 'or' }>({ items: [], logicOperator: 'and' });
  const [sortModel, setSortModel] = useState<{ field: string, sort: string }[]>([]);
  
  // Data model
  const [reportModuleCL, setReportModuleCL] = useState<NonPremiumEndorsementModel_Resp>({
    getNonPremiumEndorsement_Respdata: [],
    totalCount: 0
  });
  
  // Filter data
  const [clients, setClients] = useState([{ partnersId: 0, customerName: "" }]);
  const [states, setStates] = useState([{ stateId: 0, stateName: "" }]);
  const clientData = React.useRef(0);
  const stateData = React.useRef(0);

  // Fetch clients data
  const fetchClients = () => {
    apiClient("/Partners/getPartners", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((responseJson) => {
        if (!responseJson) return;
        setClients(responseJson.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // Fetch states data
  const fetchStates = () => {
    apiClient("/States/getStates", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((responseJson) => {
        if (!responseJson) return;
        const allowedStates = ['California', 'Nevada', 'Pennsylvania', 'Washington', 'Texas'];
        const filteredStates = responseJson.data.filter((state: any) =>
            allowedStates.includes(state.stateName)
          );
        setStates(filteredStates);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // API request function
  const getNonPremiumEndorsement = async (pageSize: number, pageNumber: number) => {
    setLoading(true);
    
    try {
      const payload: any = {
        pageSize: pageSize,
        pageNumber: pageNumber,
        field: sortModel[0]?.field || '',
        sort: sortModel[0]?.sort || '',
        status: selectedTab === 1 ? 'Outstanding' : 'Completed',
        filterModel: filterModel
      };
      
      if (clientData.current) payload.partnerId = clientData.current;
      if (stateData.current) payload.stateId = stateData.current;

      const response = await apiClient("/NonPremiumEndorsement/getNonPremiumEndorsement", {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify(payload),
      });

      if (response) {
        
        setReportModuleCL({
          getNonPremiumEndorsement_Respdata: response.data ,
          totalCount: response.totalCount || 0
        });
      }
    } catch (error) {
      console.error("Error fetching non-premium endorsements:", error);
    } finally {
      setLoading(false);
    }
  };

  // Separate function for fetching data that returns the response (for export)
  const fetchNonPremiumEndorsementData = async (pageSize: number, pageNumber: number) => {
    try {
      const payload: any = {
        pageSize: pageSize,
        pageNumber: pageNumber,
        field: sortModel[0]?.field || '',
        sort: sortModel[0]?.sort || '',
        status: selectedTab === 1 ? 'Outstanding' : 'Completed',
        filterModel: filterModel
      };
      
      if (clientData.current) payload.partnerId = clientData.current;
      if (stateData.current) payload.stateId = stateData.current;

      const response = await apiClient("/NonPremiumEndorsement/getNonPremiumEndorsement", {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify(payload),
      });

      return response;
    } catch (error) {
      console.error("Error fetching non-premium endorsements:", error);
      throw error;
    }
  };

  useEffect(() => {
    let value = 1; 
    if (params.LastTabId === 'Completed') {
      value = 2;
    }
    setSelectedTab(value);
    fetchClients();
    fetchStates();
    getNonPremiumEndorsement(_pageSize, _currentPage);
  }, []);

  useEffect(() => {
    getNonPremiumEndorsement(_pageSize, _currentPage);
  }, [selectedTab, _currentPage, _pageSize, sortModel, filterModel]);

  const handleSearch = () => {
    setCurrentPage(1);
    getNonPremiumEndorsement(_pageSize, 1);
  };

  const handleClear = () => {
    setSelectedClient(0);
    setSelectedHomeState(0);
    clientData.current = 0;
    stateData.current = 0;
    setFilterModel({ items: [] });
    setCurrentPage(1);
    getNonPremiumEndorsement(_pageSize, 1);
  };

  const handleMarkComplete = async () => {
    setLoading(true);
    
    try {
      const nonPremiumEndorsementIds = selectedRows?.map(row =>row.nonPremiumEndorsementId).filter(id => id !== undefined);

      const payload = {
        nonPremiumEndorsementIds: nonPremiumEndorsementIds,
        filterModel: filterModel
      };

      const response = await apiClient("/NonPremiumEndorsement/updateNonPremiumEndorsementStatus", {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify(payload),
      });

      if (response && response.status === true ) {
        setSelectedRows(null);
        getNonPremiumEndorsement(_pageSize, _currentPage);
      } else {
        alert(response?.message || "Failed to mark NonPremiumEndorsements as complete.");
      }
    } catch (error) {
      console.error("Error marking NonPremiumEndorsements as complete:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    // If no rows selected, download all data (similar to Export behavior)
    if (!selectedRows || selectedRows.length === 0) {
      // For consistency with Export, we could download all data here too
      // But keeping the current behavior for now since Download is typically for files
      alert("Please select at least one row to download.");
      return;
    }

    if (selectedRows.length === 1) {
      const selectedFile = selectedRows[0];
      
      // For single row, try direct download from blobAddress first
      if (selectedFile.blobAddress) {
        try {
          // Try fetch-based download first (more reliable)
          const response = await fetch(selectedFile.blobAddress);
          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = selectedFile.document || selectedFile.documentName || 'NonPremiumEndorsement';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            console.log("Downloaded file via fetch from blob address:", selectedFile.blobAddress);
            return;
          }
        } catch (error) {
          console.warn("Fetch download failed, trying direct link:", error);
        }
        
        try {
          // Fallback: Direct download from blob address
          const link = document.createElement('a');
          link.href = selectedFile.blobAddress;
          link.download = selectedFile.document || selectedFile.documentName || 'NonPremiumEndorsement';
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          console.log("Downloaded file directly from blob address:", selectedFile.blobAddress);
          return;
        } catch (error) {
          console.warn("Direct download failed, falling back to ZIP generation:", error);
          // Fall through to ZIP generation if direct download fails
        }
      }
    }

    setLoading(true);
    
    try {
      // Extract file information from selected rows
      const selectedFiles = selectedRows.map(row => ({
        id: row.nonPremiumEndorsementId || row.id,
        document: row.document || row.documentName || "",
        fileName: row.document || row.documentName || `file_${row.nonPremiumEndorsementId || row.id}`,
        blobAddress: row.blobAddress
      })).filter(file => file.id !== undefined);

      if (selectedFiles.length === 0) {
        alert("No valid files found in selected rows.");
        return;
      }

      // Always use ZIP generation for both single and multiple files
      await downloadFilesAsZip(selectedFiles);

    } catch (error) {
      console.error("Error downloading files:", error);
      alert("An error occurred while downloading files. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const downloadFilesAsZip = async (files: { id: number | undefined; document: string; fileName: string }[]) => {
    try {
      const fileIds = files.map(file => file.id).filter(id => id !== undefined).join(",");

      if (!fileIds) {
        alert("No valid file IDs found for download.");
        return;
      }

      // Show loading dialog
      Swal.fire({
        title: 'Generating Download...',
        text: 'Please wait while we generate your download package.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Build comprehensive request payload similar to month end billing
      const requestPayload = {
        // Core context 
        fileIds: fileIds,
        userId: user?.userId,
        userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        
        // Filter context
        clientFilter: selectedClient || "",
        stateFilter: selectedHomeState || "",
        
        // Include all selected files
        includeFiles: {
          outstanding: selectedTab === 1,
          completed: selectedTab === 2,
          allSelected: true,
          NonPremiumEndorsement: true
        }
      };

      // Single API call to generate comprehensive ZIP download
      const response = await apiClient('/NonPremiumEndorsement/generate-comprehensive-non-premium-endorsement-zip', {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify(requestPayload)
      });

      setLoading(false);
      Swal.close();

      if (!response) {
        throw new Error('No response received from server');
      }

      // Check for error indicators based on actual response structure
      const hasError = response.status === false || response.resp !== 0 || response.error || (response.message && response.message.toLowerCase().includes('error'));
      
      if (hasError) {
        throw new Error(response?.message || response?.error || 'Failed to generate ZIP download');
      }

      // Handle the response based on actual backend structure - same as month end billing
      if (response.data?.zip_url) {
        // Direct download of the ZIP file
        window.open(response.data.zip_url, "_blank");
        console.log("Opened ZIP file download URL:", response.data.zip_url);
        
      } else if (response.data?.zipBase64) {
        // Convert base64 to blob and download
        const byteCharacters = atob(response.data.zipBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/zip' });
        
        const fileName = `NonPremiumEndorsements_${new Date().toISOString().split('T')[0]}.zip`;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log(`Downloaded ZIP file, Size: ${blob.size} bytes`);
        
      } else if (response.data?.downloadUrl) {
        // Direct URL download
        window.open(response.data.downloadUrl, "_blank");
        console.log("Opened download URL:", response.data.downloadUrl);
        
      } else if (response.zipBase64) {
        // Maybe the ZIP data is at the root level
        const byteCharacters = atob(response.zipBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/zip' });
        
        const fileName = `NonPremiumEndorsements_${new Date().toISOString().split('T')[0]}.zip`;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log(`Downloaded ZIP file, Size: ${blob.size} bytes`);
        
      } else {
        // Show a message about the response structure
        Swal.fire({
          title: "Response Received",
          text: `Server responded but no ZIP download data found. Message: ${response?.message || 'No message'}`,
          icon: "warning",
          confirmButtonText: "OK",
        });
        return; // Exit early since we can't process the download
      }

      // Show success message with summary
      let summaryText = response?.message || "Your Non Premium Endorsement ZIP download has been generated successfully.";
      
      // Add summary information if available
      if (response.data?.files_count) {
        summaryText += `\n\n📊 ZIP Download Summary:`;
        summaryText += `\n• Total Files Downloaded: ${response.data.files_count}`;
        summaryText += `\n• ZIP Filename: ${response.data.zip_filename || 'NonPremiumEndorsements.zip'}`;
      }

      Swal.fire({
        title: "ZIP Generated Successfully!",
        text: summaryText,
        icon: "success",
        confirmButtonText: "OK",
      });

    } catch (error) {
      console.error("Error downloading ZIP file:", error);
      
      Swal.fire({
        title: "ZIP Download Failed",
        text: error instanceof Error ? error.message : "Failed to download ZIP file. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };
  
  const handleExportPdf = async () => {
    try {
      setLoading(true);
      
      // Show loading dialog
      Swal.fire({
        title: 'Generating PDF...',
        text: 'Please wait while we generate your PDF report.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Prepare request payload - matching getNonPremiumEndorsement API structure
      const requestPayload: any = {
        pageSize: reportModuleCL.totalCount || 1000,
        pageNumber: 1,
        field: sortModel[0]?.field || '',
        sort: sortModel[0]?.sort || '',
        status: selectedTab === 1 ? 'Outstanding' : 'Completed',
        filterModel: filterModel,
        userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };
      
      // Add optional filters
      if (clientData.current) requestPayload.partnerId = clientData.current;
      if (stateData.current) requestPayload.stateId = stateData.current;
      
      // Add nonPremiumEndorsementIds if rows are selected
      if (selectedRows && selectedRows.length > 0) {
        requestPayload.nonPremiumEndorsementIds = selectedRows
          .map(row => row.nonPremiumEndorsementId || row.id)
          .filter(id => id !== undefined);
      }

      // Call the PDF generation API
      const responseJson = await apiClient('/NonPremiumEndorsement/get-non-premium-endorsement-pdf', {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify(requestPayload),
      });

      setLoading(false);
      Swal.close();

      if (!responseJson) {
        Swal.fire({
          title: 'Error!',
          text: 'Failed to get response from server.',
          icon: 'error',
          confirmButtonColor: '#02A4E3'
        });
        return;
      }
      // Handle server-side PDF generation response
      if (responseJson.status && responseJson.data && responseJson.data.pdf_url) {
        // Download PDF from the provided URL
        const link = document.createElement('a');
        link.href = responseJson.data.pdf_url;
        link.download = responseJson.data.filename || 'non-premium-endorsement-report.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Show success message
        Swal.fire({
          title: 'Success!',
          text: `PDF downloaded successfully!`,
          icon: 'success',
          confirmButtonColor: '#02A4E3',
          timer: 3000,
          timerProgressBar: true
        });
        return;
      } else {
        // Invalid response format
        Swal.fire({
          title: 'Error!',
          text: 'Invalid response format from server. Please contact support.',
          icon: 'error',
          confirmButtonColor: '#02A4E3'
        });
        return;
      }
    } catch (error) {
      setLoading(false);
      Swal.close();
      
      Swal.fire({
        title: 'Error!',
        text: 'An error occurred while generating the PDF. Please try again.',
        icon: 'error',
        confirmButtonColor: '#02A4E3'
      });
      return;
    }
  };
  return (
    <Box>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="card-title">
          <span className="d-flex">Non Premium Endorsement</span>
        </div>
      </div>
      <div style={{ position: "fixed", top: "0", left: "0", zIndex: "9990", width: "100%" }}>
        {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
      </div>
      <Box sx={{ width: "100%" }}>
        <div className="col-lg-12">
          <div
            className="d-flex"
            style={{ marginBottom: "28px" }} role="group"
            aria-label="Basic example"
          >
            <React.Fragment>
              <div className="col-6 text-center">
                <button type="button"
                  className={`btn-tab btn ${selectedTab === 1 ? "btn-Selected" : "btn-primary1"}`}
                  onClick={() => {
                    setSelectedTab(1);
                  }}
                >
                  Outstanding
                </button>
              </div>
              <div className="col-6 text-center">
                <button type="button"
                  className={`btn-tab btn ${selectedTab === 2 ? "btn-Selected" : "btn-primary1"}`}
                  onClick={() => {
                    setSelectedTab(2);
                  }}
                >
                  Completed
                </button>
              </div>
            </React.Fragment>
          </div>
        </div>

        <Box sx={{ width: "100%" }}>
          <Box>
            <Grid container spacing={2} className="filter-grid" display="flex" justifyContent="center" alignItems="center">
              <Grid item xl={2} lg={4} sm={6} xs={12}>
                <FormControl fullWidth>
                  {/* <InputLabel id="client-select-label">Client</InputLabel> */}
                  <Select 
                    value={selectedClient.toString()} 
                    onChange={(event: SelectChangeEvent) => {
                      const value = Number(event.target.value);
                      setSelectedClient(value); 
                      clientData.current = value;
                    }}
                    IconComponent={ExpandMoreIcon}
                    className="border-round custom-menu-item"
                    displayEmpty
                    size="small"
                  >
                    <MenuItem key="all-clients" value={0} className="custom-menu-item">
                      Client
                    </MenuItem>
                    {clients.map((client, key) => (
                      <MenuItem key={key} value={client.partnersId} className="custom-menu-item">
                        {client.customerName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xl={2} lg={4} sm={6} xs={12}>
                <FormControl fullWidth>
                  {/* <InputLabel id="state-select-label">Home State</InputLabel> */}
                  <Select 
                    value={selectedHomeState.toString()} 
                    onChange={(event: SelectChangeEvent) => {
                      const value = Number(event.target.value);
                      setSelectedHomeState(value);
                      stateData.current = value;
                    }}
                    IconComponent={ExpandMoreIcon}
                    className="border-round custom-menu-item"
                    displayEmpty
                    size="small"
                  >
                    <MenuItem key="all-states" value={0} className="custom-menu-item">
                      Home State
                    </MenuItem>
                    {states.map((state, key) => (
                      <MenuItem key={key} value={state.stateId} className="custom-menu-item">
                        {state.stateName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xl={0.7} sm={12} xs={12}>
                <FormControl fullWidth>
                  <div>
                    <button type="button" onClick={() => { handleSearch(); }} className="active-btn1-blue-tabs" >
                      Search
                    </button>
                  </div>
                </FormControl>
              </Grid>

              <Grid item xl={0.7} sm={12} xs={12} alignItems="center">
                <FormControl fullWidth>
                  <div>
                    <button type="button" onClick={() => { handleClear(); }} className="btn-clear-refresh btn" >
                      Clear
                    </button>
                  </div>
                </FormControl>
              </Grid>

              <Grid item xl={0.7} sm={12} xs={12} alignItems="center">
                <FormControl fullWidth>
                  <div>
                    <button type="button" onClick={()=>{window.location.reload()}} className="btn-clear-refresh btn" >
                      Refresh
                    </button>
                  </div>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>

      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="card-title"></div>
        <div className="d-flex align-items-center mt-2">
          {selectedRows && selectedRows.length > 0 && selectedTab === 1 && (
            <button className="ms-2 active-btn-red" onClick={handleMarkComplete} title="Mark Complete">
              Mark Complete
            </button>
          )}
          {selectedRows && selectedRows.length > 0 && (
          <button
            className="ms-2 active-btn-blue-export-12"
            onClick={() => { handleDownload() }}
            title="Download">
            Download
          </button>
          )}
          <button
            className="ms-2 active-btn-blue-export-12"
            onClick={() => { handleExportPdf() }}
            title="Export PDF">
            Export
          </button>
        </div>
      </div>

     {/* Data Grid */}
     <NonPremiumEndorsementList
        ReportModuleCL={reportModuleCL}
        setSelectedRows={setSelectedRows}
        selectedRows={selectedRows}
        setCurrentPage={setCurrentPage}
        setPageSize={setPageSize}
        _pageSize={_pageSize}
        filterModel={filterModel}
        setFilterModel={setFilterModel}
        setSortModel={setSortModel}
        loading={loading}
        selectedTab={selectedTab}
     />
   </Box>
 );
};

export default NonPremiumEndorsement;